import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Router } from '@angular/router';

export interface AiPanelState {
  isOpen: boolean;
}

export type AiContextType = 'general' | 'page-editor';

export interface AiPanelContext {
  type: AiContextType;
  // Page editor context
  pageId?: string;
  pageName?: string;
  tenantId?: string;
  userId?: string;
  controls?: any[];
  html?: string;
  footerHtml?: string;
  css?: string;
  script?: string;
  corePreferences?: any[];
  coreContacts?: any[];
}

export interface AiQuickAction {
  icon: string;
  label: string;
  prompt: string;
}

/** Emitted by the AI panel when the editor should apply changes */
export interface AiPageChanges {
  controls?: any[];
  headerHtml?: string;
  footerHtml?: string;
  css?: string;
  javascript?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AiPanelService {
  private panelStateSubject = new BehaviorSubject<AiPanelState>({
    isOpen: false
  });

  private contextSubject = new BehaviorSubject<AiPanelContext>({ type: 'general' });

  /** Emits when the AI panel wants the editor to apply changes */
  private applyChangesSubject = new Subject<AiPageChanges>();

  panelState$ = this.panelStateSubject.asObservable();
  context$ = this.contextSubject.asObservable();
  applyChanges$ = this.applyChangesSubject.asObservable();

  constructor(private router: Router) { }

  get currentContext(): AiPanelContext {
    return this.contextSubject.value;
  }

  setPanelState(state: Partial<AiPanelState>): void {
    this.panelStateSubject.next({
      ...this.panelStateSubject.value,
      ...state
    });
  }

  openPanel(): void {
    this.setPanelState({ isOpen: true });
  }

  closePanel(): void {
    this.setPanelState({ isOpen: false });
  }

  togglePanel(): void {
    const currentState = this.panelStateSubject.value;
    this.setPanelState({ isOpen: !currentState.isOpen });
  }

  /** Set the active context (called by editor components) */
  setContext(context: AiPanelContext): void {
    this.contextSubject.next(context);
  }

  /** Update specific fields of the context without replacing it */
  updateContext(partial: Partial<AiPanelContext>): void {
    this.contextSubject.next({
      ...this.contextSubject.value,
      ...partial
    });
  }

  /** Clear context back to general (called on editor destroy) */
  clearContext(): void {
    this.contextSubject.next({ type: 'general' });
  }

  /** Emit page changes for the editor to apply */
  emitPageChanges(changes: AiPageChanges): void {
    this.applyChangesSubject.next(changes);
  }

  navigate(route: string): void {
    this.router.navigateByUrl(route);
  }
}
